import moment from 'moment'

// Shared by the applicant wizard and the reviewer's application page so both
// agree on what counts as a gap, a covered address history and a valid
// licence tail.

export const ADDRESS_HISTORY_YEARS = 5
// Anything shorter than this between jobs is a notice period / changeover,
// not a gap that needs explaining.
const GAP_TOLERANCE_DAYS = 31

export const formatAddress = (address) =>
  [
    address?.lineOne,
    address?.lineTwo,
    address?.town,
    address?.city,
    address?.county,
    address?.postCode,
  ]
    .map((part) => part?.trim?.())
    .filter(Boolean)
    .join(', ')

const jobEnd = (job) =>
  job.stillPresent ? moment() : job.endDate ? moment(job.endDate) : null

// Periods with no job recorded, oldest first — between consecutive jobs and
// from the most recent job to today. Overlapping jobs are merged first so
// holding two jobs at once never reads as a gap.
export const employmentGaps = (workExperience = []) => {
  const jobs = workExperience
    .filter((job) => job?.startDate && moment(job.startDate).isValid())
    .filter((job) => jobEnd(job)?.isValid())
    .map((job) => ({ start: moment(job.startDate), end: jobEnd(job) }))
    .sort((a, b) => a.start - b.start)
  if (!jobs.length) return []

  const gaps = []
  let coveredUntil = jobs[0].end
  for (const job of jobs.slice(1)) {
    if (job.start.diff(coveredUntil, 'days') > GAP_TOLERANCE_DAYS) {
      gaps.push({ from: coveredUntil.clone(), to: job.start.clone() })
    }
    if (job.end.isAfter(coveredUntil)) coveredUntil = job.end
  }
  if (moment().diff(coveredUntil, 'days') > GAP_TOLERANCE_DAYS) {
    gaps.push({ from: coveredUntil.clone(), to: null })
  }
  return gaps
}

export const describeGap = ({ from, to }) =>
  `${from.format('MMM YYYY')} – ${to ? to.format('MMM YYYY') : 'present'}`

// How far back the applicant's addresses reach, and whether that covers the
// five years a DBS application asks for.
export const addressCoverage = (currentAddressSince, addressHistory = []) => {
  const starts = [currentAddressSince, ...addressHistory.map((a) => a?.from)]
    .filter(Boolean)
    .map((date) => moment(date))
    .filter((date) => date.isValid())
  const coveredFrom = starts.length ? moment.min(starts) : null
  const needed = moment().subtract(ADDRESS_HISTORY_YEARS, 'years')
  return {
    coveredFrom,
    // Only meaningful once the move-in date is known
    needsHistory:
      !!currentAddressSince &&
      moment(currentAddressSince).isAfter(needed, 'day'),
    isCovered: !!coveredFrom && !coveredFrom.isAfter(needed, 'day'),
  }
}

// The DVLA prints the 16-character licence number in field 5 of the
// photocard; only the last 8 are needed alongside a check code. Pasting the
// whole number keeps its tail (matching the server's normalisation).
export const normaliseLicenceTail = (value = '') =>
  value.replace(/\s+/g, '').toUpperCase().slice(-8)

export const isValidLicenceTail = (value) => /^[A-Z0-9]{8}$/.test(value ?? '')
